import { stpFutureValue } from "../../engine/calculations";

// Task 16.1 — one STP, seen every way: the source fund depleting, the target fund growing, the
// combined value, and a year-by-year schedule. Composes stpFutureValue (which already returns a
// month-by-month schedule) — no new engine maths.
const config = {
  key: "stp-comprehensive",
  title: "STP Comprehensive",
  category: "Comprehensive & Reports",
  description: "One STP, seen every way: source fund depleting, target fund growing, combined value, and a year-by-year schedule.",
  fields: [
    { key: "sourceCorpus", label: "Starting source corpus", type: "currency", default: 50_00_000, min: 1000, max: 50_00_00_000, help: "Usually parked in a debt/liquid fund, transferred gradually into equity." },
    { key: "monthlyTransfer", label: "Monthly transfer (STP)", type: "currency", default: 50_000, min: 500, max: 50_00_000 },
    { key: "sourceRate", label: "Source fund return", type: "percent", default: 6, min: 0, max: 20 },
    { key: "targetRate", label: "Target fund return", type: "percent", default: 12, min: 1, max: 30, assumptionKey: "equityReturn" },
    { key: "years", label: "STP period (years)", type: "number", default: 5, min: 1, max: 40 },
  ],
  chart: {
    type: "line",
    xKey: "year",
    series: [
      { key: "source", label: "Source fund", colorVar: "--muted" },
      { key: "target", label: "Target fund", colorVar: "--gold" },
    ],
  },
  compute(values) {
    const sourceCorpus = Number(values.sourceCorpus) || 0;
    const monthlyTransfer = Number(values.monthlyTransfer) || 0;
    const sourceRate = (Number(values.sourceRate) || 0) / 100;
    const targetRate = (Number(values.targetRate) || 0) / 100;
    const years = Number(values.years) || 0;
    const months = years * 12;

    const result = stpFutureValue(sourceCorpus, sourceRate, targetRate, monthlyTransfer, months);
    const total = result.finalSource + result.finalTarget;

    // stpFutureValue returns a per-month schedule; sample it at each year-end for the view.
    const series = [];
    const rows = [];
    for (let y = 1; y <= years; y++) {
      const snapshot = result.schedule[y * 12 - 1];
      const source = snapshot ? snapshot.source : 0;
      const target = snapshot ? snapshot.target : 0;
      series.push({ year: y, source, target });
      rows.push({ year: y, source, target, total: source + target });
    }

    return {
      headline: { label: "Combined value at the end", value: total },
      extraHeadlines: [
        { label: "Target fund built", value: result.finalTarget },
        { label: "Source fund remaining", value: result.finalSource },
      ],
      breakdown: [
        { label: "Monthly transfer", value: monthlyTransfer },
        { label: "STP period", value: `${years} years`, format: "text" },
      ],
      series,
      table: {
        title: "Year-by-year",
        columns: [
          { key: "year", label: "Year", format: "text" },
          { key: "source", label: "Source fund" },
          { key: "target", label: "Target fund" },
          { key: "total", label: "Combined" },
        ],
        rows,
      },
    };
  },
};

export default config;
